import React, { useState } from "react";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "./ui/dialog";
import { Input } from "./ui/input";
import { Textarea } from "./ui/textarea";
import { Button } from "./ui/button";
import { useTheme } from "./ThemeContext";
import { MagicCard } from "./ui/magic-card";
import {
  Card,
  CardContent,
  CardDescription,
  CardFooter,
  CardHeader,
  CardTitle,
} from "./ui/card";
import { Label } from "./ui/label";
import { submitN8nForm, N8nFormType } from "@/services/n8nService";

interface DemoRequestDialogProps {
  children: React.ReactNode;
  triggerText?: string;
  source?: string;
  type?: N8nFormType;
}

export function DemoRequestDialog({
  children,
  triggerText = "Request Demo",
  source,
  type = "lead",
}: DemoRequestDialogProps) {
  const { theme } = useTheme();
  const [open, setOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const [formData, setFormData] = useState({
    name: "",
    email: "",
    phone: "",
    organization: "",
    address: "",
    zip_code: "",
    message: "",
  });
  const [status, setStatus] = useState<{
    type: "success" | "error" | null;
    message: string;
  }>({ type: null, message: "" });

  const handleInputChange = (
    e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>
  ) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
  };

  const determineSource = () => {
    if (source) return source;
    const lower = triggerText.toLowerCase();
    if (lower.includes("get started")) return "provider_get_started";
    if (lower.includes("demo")) return "provider_schedule_demo";
    if (lower.includes("founder")) return "investor_contact";
    return "provider_schedule_demo";
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setLoading(true);
    setStatus({ type: null, message: "" });

    try {
      const resolvedSource = determineSource();

      const result = await submitN8nForm({
        type,
        source: resolvedSource,
        name: formData.name.trim() || null,
        email: formData.email.trim() || null,
        phone: formData.phone.trim() || null,
        organization_name: formData.organization.trim() || null,
        clinic_name: formData.organization.trim() || null,
        address: formData.address.trim() || null,
        zip_code: formData.zip_code.trim() || null,
        message: formData.message.trim() || null,
        topic: triggerText,
      });

      if (result.success) {
        setStatus({
          type: "success",
          message: "Thank you! We'll be in touch with you shortly.",
        });
        setFormData({
          name: "",
          email: "",
          phone: "",
          organization: "",
          address: "",
          zip_code: "",
          message: "",
        });

        setTimeout(() => {
          setOpen(false);
          setStatus({ type: null, message: "" });
        }, 2200);
      } else {
        setStatus({
          type: "error",
          message: result.error || "Something went wrong. Please try again.",
        });
      }
    } catch (error: any) {
      console.error("Submission Error:", error);
      setStatus({
        type: "error",
        message: "Something went wrong. Please try again.",
      });
    } finally {
      setLoading(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>{children}</DialogTrigger>
      <DialogContent className="p-0 overflow-hidden border-none bg-transparent max-w-[calc(100%-1.5rem)] sm:max-w-xl">
        {/* The card scrolls (not the dialog) so the close button stays pinned on long forms / small phones */}
        <Card className="w-full max-w-xl max-h-[90dvh] overflow-y-auto border-none p-0 shadow-none backdrop-blur-xl bg-slate-50/95 dark:bg-slate-900/95">
          <MagicCard
            className="p-0 overflow-hidden"
            gradientColor={theme === "dark" ? "#211639ff" : "#D9D9D955"}
            gradientFrom="#3b82f6"
            gradientTo="#9333ea"
            gradientSize={400}
            mode="gradient"
          >
            <form onSubmit={handleSubmit}>
              <CardHeader className="border-border border-b p-6 [.border-b]:pb-6">
                <CardTitle
                  className={`${
                    theme === "dark" ? "text-white" : "text-slate-900"
                  } text-2xl`}
                >
                  {triggerText}
                </CardTitle>
                <CardDescription
                  className={`${
                    theme === "dark" ? "text-slate-400" : "text-slate-600"
                  }`}
                >
                  Fill out the form below and our team will get back to you shortly.
                </CardDescription>
              </CardHeader>

              <CardContent className="p-6 space-y-4">
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                  <div className="grid gap-2">
                    <Label
                      htmlFor="name"
                      className={`${
                        theme === "dark" ? "text-slate-300" : "text-slate-700"
                      }`}
                    >
                      Name *
                    </Label>
                    <Input
                      id="name"
                      name="name"
                      type="text"
                      placeholder="Jane Doe"
                      value={formData.name}
                      onChange={handleInputChange}
                      required
                      className={`${
                        theme === "dark"
                          ? "bg-slate-800/50 border-slate-700 text-white placeholder:text-slate-500"
                          : "bg-white border-slate-300 text-slate-900 placeholder:text-slate-400"
                      }`}
                    />
                  </div>

                  <div className="grid gap-2">
                    <Label
                      htmlFor="email"
                      className={`${
                        theme === "dark" ? "text-slate-300" : "text-slate-700"
                      }`}
                    >
                      Email *
                    </Label>
                    <Input
                      id="email"
                      name="email"
                      type="email"
                      placeholder="jane@clinic.com"
                      value={formData.email}
                      onChange={handleInputChange}
                      required
                      className={`${
                        theme === "dark"
                          ? "bg-slate-800/50 border-slate-700 text-white placeholder:text-slate-500"
                          : "bg-white border-slate-300 text-slate-900 placeholder:text-slate-400"
                      }`}
                    />
                  </div>
                </div>

                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                  <div className="grid gap-2">
                    <Label
                      htmlFor="organization"
                      className={`${
                        theme === "dark" ? "text-slate-300" : "text-slate-700"
                      }`}
                    >
                      Organization / Clinic *
                    </Label>
                    <Input
                      id="organization"
                      name="organization"
                      type="text"
                      placeholder="Acme Health Clinic"
                      value={formData.organization}
                      onChange={handleInputChange}
                      required
                      className={`${
                        theme === "dark"
                          ? "bg-slate-800/50 border-slate-700 text-white placeholder:text-slate-500"
                          : "bg-white border-slate-300 text-slate-900 placeholder:text-slate-400"
                      }`}
                    />
                  </div>

                  <div className="grid gap-2">
                    <Label
                      htmlFor="phone"
                      className={`${
                        theme === "dark" ? "text-slate-300" : "text-slate-700"
                      }`}
                    >
                      Phone Number
                    </Label>
                    <Input
                      id="phone"
                      name="phone"
                      type="tel"
                      placeholder="(555) 000-0000"
                      value={formData.phone}
                      onChange={handleInputChange}
                      className={`${
                        theme === "dark"
                          ? "bg-slate-800/50 border-slate-700 text-white placeholder:text-slate-500"
                          : "bg-white border-slate-300 text-slate-900 placeholder:text-slate-400"
                      }`}
                    />
                  </div>
                </div>

                <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
                  <div className="sm:col-span-2 grid gap-2">
                    <Label
                      htmlFor="address"
                      className={`${
                        theme === "dark" ? "text-slate-300" : "text-slate-700"
                      }`}
                    >
                      Address
                    </Label>
                    <Input
                      id="address"
                      name="address"
                      type="text"
                      placeholder="123 Medical Center Dr"
                      value={formData.address}
                      onChange={handleInputChange}
                      className={`${
                        theme === "dark"
                          ? "bg-slate-800/50 border-slate-700 text-white placeholder:text-slate-500"
                          : "bg-white border-slate-300 text-slate-900 placeholder:text-slate-400"
                      }`}
                    />
                  </div>

                  <div className="grid gap-2">
                    <Label
                      htmlFor="zip_code"
                      className={`${
                        theme === "dark" ? "text-slate-300" : "text-slate-700"
                      }`}
                    >
                      Zip Code
                    </Label>
                    <Input
                      id="zip_code"
                      name="zip_code"
                      type="text"
                      placeholder="78701"
                      value={formData.zip_code}
                      onChange={handleInputChange}
                      className={`${
                        theme === "dark"
                          ? "bg-slate-800/50 border-slate-700 text-white placeholder:text-slate-500"
                          : "bg-white border-slate-300 text-slate-900 placeholder:text-slate-400"
                      }`}
                    />
                  </div>
                </div>

                <div className="grid gap-2">
                  <Label
                    htmlFor="message"
                    className={`${
                      theme === "dark" ? "text-slate-300" : "text-slate-700"
                    }`}
                  >
                    Message / Notes
                  </Label>
                  <Textarea
                    id="message"
                    name="message"
                    rows={3}
                    placeholder="Tell us about your clinic, patient volume, or questions..."
                    value={formData.message}
                    onChange={handleInputChange}
                    className={`${
                      theme === "dark"
                        ? "bg-slate-800/50 border-slate-700 text-white placeholder:text-slate-500"
                        : "bg-white border-slate-300 text-slate-900 placeholder:text-slate-400"
                    }`}
                  />
                </div>

                {status.type && (
                  <div
                    className={`p-3 rounded-xl text-sm ${
                      status.type === "success"
                        ? theme === "dark"
                          ? "bg-green-900/20 text-green-400 border border-green-800"
                          : "bg-green-50 text-green-700 border border-green-200"
                        : theme === "dark"
                        ? "bg-red-900/20 text-red-400 border border-red-800"
                        : "bg-red-50 text-red-700 border border-red-200"
                    }`}
                  >
                    {status.message}
                  </div>
                )}
              </CardContent>

              <CardFooter className="border-border border-t p-6 [.border-t]:pt-6">
                <Button
                  type="submit"
                  disabled={loading}
                  className="w-full btn-primary text-white py-5 rounded-xl text-base font-medium shadow-lg shadow-purple-500/20"
                >
                  {loading ? "Submitting..." : "Submit"}
                </Button>
              </CardFooter>
            </form>
          </MagicCard>
        </Card>
      </DialogContent>
    </Dialog>
  );
}
